import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Lock,
  LayoutDashboard,
  Users,
  Package,
  ShoppingBag,
  ArrowRight,
  Plus,
  Pencil,
  Trash2,
  Mail,
  MessageSquare,
  Tag,
  X,
  CheckCircle2,
  Eye,
  Ruler,
  Layers,
  Sparkles,
  RotateCcw,
  Grid2x2,
  ChevronDown,
  ChevronRight,
  Globe,
  Box,
  BarChart3,
  UserCog,
  Building2,
  ClipboardList,
  Boxes,
  Users2,
  Inbox,
  Camera as CameraIcon,
  Store as StoreIcon,
  Mail as MailIcon,
  CalendarClock,
  Calculator,
  Banknote,
} from "lucide-react";
import {
  adminLogin,
  getAdminToken,
  saveAdminToken,
  saveAdminUser,
  getAdminUser,
  clearAdminToken,
  adminGetStats,
  adminListSessions,
  adminGetSession,
  adminListOrders,
  adminListProducts,
  adminDeleteProduct,
  adminListSizes,
  adminCreateSize,
  adminUpdateSize,
  adminDeleteSize,
  adminListPapers,
  adminCreatePaper,
  adminUpdatePaper,
  adminDeletePaper,
  adminListFinishes,
  adminCreateFinish,
  adminUpdateFinish,
  adminDeleteFinish,
  adminReseedCatalog,
} from "../lib/admin";
import { parseQuoteBlock, stripQuoteBlock } from "../lib/session";
import ProductEditor from "../components/admin/ProductEditor";
import LibraryTab from "../components/admin/LibraryTab";
import PricingMatrixTab from "../components/admin/PricingMatrixTab";
import DomainsTab from "../components/admin/DomainsTab";
import TeamMembersTab from "../components/admin/TeamMembersTab";
import TrackerTab from "../components/admin/tracker/TrackerTab";
import ReportsTab from "../components/admin/tracker/ReportsTab";
import SuppliersTab from "../components/admin/inventory/SuppliersTab";
import MaterialsTab from "../components/admin/inventory/MaterialsTab";
import InventoryOrdersTab from "../components/admin/inventory/InventoryOrdersTab";
import CustomersTab from "../components/admin/customers/CustomersTab";
import EmailIntakeTab from "../components/admin/intake/EmailIntakeTab";
import MaterialLibraryTab from "../components/admin/library/MaterialLibraryTab";
import PortfolioTab from "../components/admin/portfolio/PortfolioTab";
import StoresTab from "../components/admin/stores/StoresTab";
import EmailTemplatesTab from "../components/admin/templates/EmailTemplatesTab";
import TeamTab from "../components/admin/team/TeamTab";
import CRMTab from "../components/admin/crm/CRMTab";
import TeamsScheduleTab from "../components/admin/teams/TeamsScheduleTab";
import ClosingsTab from "../components/admin/closings/ClosingsTab";
import InternalQuoteTab from "../components/admin/internal-quote/InternalQuoteTab";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/* =========================== Login =========================== */
const AdminLogin = ({ onAuthed }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { token, user } = await adminLogin(username.trim(), password);
      saveAdminToken(token);
      saveAdminUser(user);
      toast.success(`Welcome, ${user.username}.`);
      onAuthed(user);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid username or password";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center grid-bg p-6">
      <div className="w-full max-w-sm">
        <div className="overline mb-3">Restricted · Admin</div>
        <h1 className="font-display text-4xl font-black tracking-tight leading-none mb-6">
          Hub Control Panel
        </h1>
        <form onSubmit={submit} className="space-y-4 card-swiss p-6 rounded-sm" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Username</label>
            <input
              type="text"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-swiss w-full px-3 py-3 rounded-sm"
              placeholder="owner"
              data-testid="admin-username-input"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Password</label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]"
              />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-swiss w-full pl-9 pr-3 py-3 rounded-sm"
                placeholder="••••••••"
                data-testid="admin-password-input"
                required
              />
            </div>
            {err && <p className="text-xs text-red-700 mt-2" data-testid="admin-error">{err}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-brutal w-full py-3 px-4 rounded-sm flex items-center justify-center gap-2"
            data-testid="admin-login-button"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : <>Unlock <ArrowRight size={16} /></>}
          </button>
        </form>
        <p className="mt-4 text-[11px] font-mono text-[color:var(--ups-subtext)] text-center">
          Forgot your password? The owner can reset it from the Team tab.
        </p>
      </div>
    </div>
  );
};

/* =========================== Stats =========================== */
const StatCard = ({ label, value, icon: Icon, accent = "var(--ups-gold)" }) => (
  <div className="card-swiss p-5 rounded-sm">
    <div className="flex items-center justify-between">
      <div className="overline">{label}</div>
      <div
        className="w-7 h-7 flex items-center justify-center"
        style={{ background: accent, color: "var(--ups-ink)" }}
      >
        <Icon size={14} strokeWidth={2.5} />
      </div>
    </div>
    <div className="mt-2 font-display text-3xl font-black tracking-tight">{value}</div>
  </div>
);

const Overview = ({ stats }) => {
  if (!stats) return null;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Sessions" value={stats.sessions} icon={Users} />
        <StatCard label="Messages" value={stats.messages} icon={MessageSquare} />
        <StatCard label="Orders" value={stats.orders} icon={ShoppingBag} accent="#16A34A" />
        <StatCard label="Products" value={stats.products} icon={Package} accent="var(--ups-espresso)" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-swiss p-5 rounded-sm">
          <div className="overline">Retail Quoted</div>
          <div className="mt-1 font-display text-2xl font-bold line-through text-[color:var(--ups-subtext)]">
            {fmtUSD(stats.revenue_retail)}
          </div>
        </div>
        <div
          className="p-5 rounded-sm"
          style={{ background: "var(--ups-muted)", border: "2px solid var(--ups-ink)" }}
        >
          <div className="overline">UPS Store Revenue</div>
          <div className="mt-1 font-display text-2xl font-black" style={{ color: "var(--ups-accent)" }}>
            {fmtUSD(stats.revenue_ups)}
          </div>
        </div>
        <div className="card-swiss p-5 rounded-sm">
          <div className="overline">Savings Delivered</div>
          <div className="mt-1 font-display text-2xl font-bold">{fmtUSD(stats.savings)}</div>
        </div>
      </div>
    </div>
  );
};

/* =========================== Sessions =========================== */
const SessionDetail = ({ sessionId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await adminGetSession(sessionId));
      } catch {
        toast.error("Failed to load session");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="session-detail-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative ml-auto w-full sm:max-w-2xl bg-[color:var(--ups-paper)] h-full overflow-y-auto border-l-2 border-[color:var(--ups-ink)]">
        <div className="sticky top-0 z-10 bg-[color:var(--ups-ink)] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "#FFB500" }}>Session</div>
            <h3 className="font-display text-lg font-bold font-mono">
              {sessionId.slice(0, 8)}…
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : data ? (
          <div className="p-5 space-y-5">
            <div className="card-swiss p-4 rounded-sm">
              <div className="overline">Customer</div>
              <div className="font-mono text-sm mt-1">{data.session.email}</div>
              <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] mt-1">
                Started {fmtDate(data.session.created_at)}
              </div>
            </div>

            {data.quotes.length > 0 && (
              <div>
                <div className="overline mb-2">Quotes Generated ({data.quotes.length})</div>
                <div className="space-y-2">
                  {data.quotes.map((q) => (
                    <div key={q.message_id} className="card-swiss p-3 rounded-sm flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display font-bold text-sm">{q.quote.product_name}</div>
                        <div className="text-xs text-[color:var(--ups-subtext)]">{q.quote.specifications}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] text-[color:var(--ups-subtext)] line-through">{fmtUSD(q.quote.retail_price)}</div>
                        <div className="font-bold" style={{ color: "var(--ups-accent)" }}>{fmtUSD(q.quote.ups_price)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.orders.length > 0 && (
              <div>
                <div className="overline mb-2">Orders ({data.orders.length})</div>
                <div className="space-y-2">
                  {data.orders.map((o) => (
                    <div key={o.id} className="card-swiss p-3 rounded-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-mono font-bold text-sm">#{o.order_number}</div>
                          <div className="text-xs text-[color:var(--ups-subtext)]">
                            {o.full_name} · Store {o.store_number}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold" style={{ color: "var(--ups-accent)" }}>{fmtUSD(o.ups_price)}</div>
                          <div className="text-[10px] font-mono text-[color:var(--ups-subtext)]">
                            {o.attachment_names?.length || 0} files
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] font-mono text-[color:var(--ups-subtext)]">
                        email: {o.email_status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="overline mb-2">Chat Transcript ({data.messages.length})</div>
              <div className="space-y-2">
                {data.messages.map((m) => {
                  const isUser = m.role === "user";
                  const quote = !isUser ? parseQuoteBlock(m.content) : null;
                  const text = !isUser ? stripQuoteBlock(m.content) : m.content;
                  return (
                    <div
                      key={m.id}
                      className={`px-3 py-2 rounded-sm text-sm ${isUser ? "" : "card-swiss"}`}
                      style={
                        isUser
                          ? { background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }
                          : {}
                      }
                    >
                      <div className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-[color:var(--ups-subtext)] mb-1">
                        {isUser ? "Customer" : "Bot"} · {fmtDate(m.created_at)}
                      </div>
                      <div className="whitespace-pre-wrap leading-relaxed">{text}</div>
                      {quote && (
                        <div className="mt-2 text-xs font-mono px-2 py-1 inline-block" style={{ background: "#FFB500", color: "var(--ups-ink)" }}>
                          QUOTE · {quote.product_name} · {fmtUSD(quote.ups_price)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openGroups, setOpenGroups] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const { sessions } = await adminListSessions();
      setSessions(sessions);
    } catch {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      const key = (s.email || "—").toLowerCase();
      if (!map.has(key)) {
        map.set(key, { email: s.email || "—", sessions: [], message_count: 0, order_count: 0, last_activity: null, best_quote: 0 });
      }
      const g = map.get(key);
      g.sessions.push(s);
      g.message_count += s.message_count || 0;
      g.order_count += s.order_count || 0;
      if (!g.last_activity || (s.last_activity && s.last_activity > g.last_activity)) g.last_activity = s.last_activity;
      const lq = s.last_quote?.ups_price || 0;
      if (lq > g.best_quote) g.best_quote = lq;
    }
    // sort groups by last_activity desc; and sort each group's sessions desc too
    const out = Array.from(map.values());
    out.sort((a, b) => (b.last_activity || "").localeCompare(a.last_activity || ""));
    out.forEach((g) => g.sessions.sort((a, b) => (b.last_activity || "").localeCompare(a.last_activity || "")));
    return out;
  }, [sessions]);

  const toggleGroup = (email) => setOpenGroups((prev) => ({ ...prev, [email]: !prev[email] }));

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
        <Loader2 size={14} className="animate-spin" /> Loading sessions…
      </div>
    );

  const gridCols = "0.4fr 1.5fr 1fr 0.6fr 0.6fr 1fr 0.6fr";

  return (
    <>
      <div className="card-swiss rounded-sm overflow-hidden" data-testid="sessions-table">
        <div
          className="grid text-[11px] uppercase font-bold tracking-[0.15em] py-3 px-4 border-b"
          style={{ gridTemplateColumns: gridCols, borderColor: "var(--ups-border)", background: "var(--ups-muted)" }}
        >
          <div></div>
          <div>Customer</div>
          <div>Last Activity</div>
          <div className="text-right">Msgs</div>
          <div className="text-right">Orders</div>
          <div className="text-right">Best Quote</div>
          <div className="text-right">Sessions</div>
        </div>
        {groups.length === 0 && (
          <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No sessions yet.</div>
        )}
        {groups.map((g) => {
          const isOpen = !!openGroups[g.email];
          return (
            <div key={g.email} data-testid={`session-group-${g.email}`}>
              <div
                className="grid text-sm py-3 px-4 border-b hover:bg-[color:var(--ups-muted)] transition cursor-pointer"
                style={{ gridTemplateColumns: gridCols, borderColor: "var(--ups-border)" }}
                onClick={() => toggleGroup(g.email)}
                data-testid={`session-group-row-${g.email}`}
              >
                <div className="self-center">
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
                <div className="self-center font-mono font-bold text-sm">{g.email}</div>
                <div className="font-mono text-xs text-[color:var(--ups-subtext)] self-center">
                  {fmtDate(g.last_activity)}
                </div>
                <div className="text-right self-center font-mono">{g.message_count}</div>
                <div className="text-right self-center font-mono">{g.order_count}</div>
                <div className="text-right self-center">
                  {g.best_quote > 0 ? (
                    <span className="font-bold" style={{ color: "var(--ups-accent)" }}>
                      {fmtUSD(g.best_quote)}
                    </span>
                  ) : (
                    <span className="text-[11px] text-[color:var(--ups-subtext)]">—</span>
                  )}
                </div>
                <div className="text-right self-center font-mono">
                  <span
                    className="inline-block px-2 py-0.5 text-[11px] font-bold"
                    style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
                  >
                    {g.sessions.length}
                  </span>
                </div>
              </div>
              {isOpen &&
                g.sessions.map((s) => (
                  <div
                    key={s.session_id}
                    className="grid text-sm py-2.5 px-4 border-b hover:bg-[color:var(--ups-muted)] transition cursor-pointer"
                    style={{ gridTemplateColumns: gridCols, borderColor: "var(--ups-border)", background: "#fafaf9" }}
                    onClick={() => setOpenId(s.session_id)}
                    data-testid={`session-row-${s.session_id}`}
                  >
                    <div></div>
                    <div className="pl-4 border-l-2" style={{ borderColor: "var(--ups-gold)" }}>
                      <div className="font-mono text-[11px] text-[color:var(--ups-subtext)]">
                        session · {s.session_id.slice(0, 8)}…
                      </div>
                      <div className="text-[11px] text-[color:var(--ups-subtext)]">
                        started {fmtDate(s.created_at)}
                      </div>
                    </div>
                    <div className="font-mono text-xs text-[color:var(--ups-subtext)] self-center">
                      {fmtDate(s.last_activity)}
                    </div>
                    <div className="text-right self-center font-mono">{s.message_count}</div>
                    <div className="text-right self-center font-mono">{s.order_count}</div>
                    <div className="text-right self-center">
                      {s.last_quote ? (
                        <span className="font-bold" style={{ color: "var(--ups-accent)" }}>
                          {fmtUSD(s.last_quote.ups_price)}
                        </span>
                      ) : (
                        <span className="text-[11px] text-[color:var(--ups-subtext)]">—</span>
                      )}
                    </div>
                    <div className="text-right self-center">
                      <Eye size={16} className="inline" />
                    </div>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
      {openId && <SessionDetail sessionId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
};

/* =========================== Orders =========================== */
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { orders } = await adminListOrders();
        setOrders(orders);
      } catch {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
        <Loader2 size={14} className="animate-spin" /> Loading orders…
      </div>
    );

  if (!orders.length)
    return <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No orders yet.</div>;

  return (
    <div className="card-swiss rounded-sm overflow-hidden" data-testid="orders-table">
      <div
        className="grid text-[11px] uppercase font-bold tracking-[0.15em] py-3 px-4 border-b"
        style={{ gridTemplateColumns: "0.7fr 1.4fr 0.7fr 1fr 0.8fr 0.8fr 0.8fr", borderColor: "var(--ups-border)", background: "var(--ups-muted)" }}
      >
        <div>Order #</div>
        <div>Customer</div>
        <div>Store</div>
        <div>Date</div>
        <div className="text-right">Retail</div>
        <div className="text-right">UPS</div>
        <div className="text-right">Email</div>
      </div>
      {orders.map((o) => (
        <div
          key={o.id}
          className="grid text-sm py-3 px-4 border-b"
          style={{ gridTemplateColumns: "0.7fr 1.4fr 0.7fr 1fr 0.8fr 0.8fr 0.8fr", borderColor: "var(--ups-border)" }}
          data-testid={`order-row-${o.id}`}
        >
          <div className="font-mono font-bold">#{o.order_number}</div>
          <div>
            <div className="text-sm">{o.full_name}</div>
            <div className="text-[10px] font-mono text-[color:var(--ups-subtext)]">{o.email}</div>
          </div>
          <div className="font-mono text-xs self-center">{o.store_number}</div>
          <div className="font-mono text-xs text-[color:var(--ups-subtext)] self-center">{fmtDate(o.created_at)}</div>
          <div className="text-right self-center line-through text-[color:var(--ups-subtext)]">{fmtUSD(o.retail_price)}</div>
          <div className="text-right self-center font-bold" style={{ color: "var(--ups-accent)" }}>
            {fmtUSD(o.ups_price)}
          </div>
          <div className="text-right self-center">
            {o.email_status === "sent" ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: "var(--ups-accent)" }}>
                <CheckCircle2 size={12} /> sent
              </span>
            ) : (
              <span className="text-[10px] font-mono text-[color:var(--ups-subtext)]">{String(o.email_status).slice(0, 18)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================== Products =========================== */
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or {} for new, or product object for edit

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await adminListProducts();
      setProducts(list);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminDeleteProduct(id);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-sm text-[color:var(--ups-subtext)]">
          The bot uses this catalog when quoting. Pricing is computed deterministically from size × paper × finishes × quantity.
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={async () => {
              if (!window.confirm("Wipe & re-seed the full catalog (sizes, papers, finishes, products)? All current entries will be lost.")) return;
              try {
                const res = await adminReseedCatalog();
                toast.success(`Re-seeded: ${res.products} products, ${res.sizes} sizes, ${res.papers} papers, ${res.finishes} finishes`);
                refresh();
              } catch {
                toast.error("Re-seed failed");
              }
            }}
            className="btn-ink px-3 py-2 rounded-sm flex items-center gap-2 text-sm"
            data-testid="reseed-button"
          >
            <RotateCcw size={14} /> Re-seed
          </button>
          <button
            onClick={() => setEditing({})}
            className="btn-brutal px-3 py-2 rounded-sm flex items-center gap-2 text-sm"
            data-testid="new-product-button"
          >
            <Plus size={16} /> New Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
          <Loader2 size={14} className="animate-spin" /> Loading products…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="products-grid">
          {products.map((p) => {
            const sizes = [...(p.allowed_size_ids || []), ...((p.custom_sizes || []).map((s) => s.id))];
            const paperCount = (p.allowed_paper_ids || []).length + (p.custom_papers || []).length;
            const finishCount = (p.allowed_finish_ids || []).length + (p.required_finish_ids || []).length;
            return (
              <div key={p.id} className="card-swiss p-4 rounded-sm" data-testid={`product-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: p.is_active ? "#FFB500" : "var(--ups-muted)", color: "var(--ups-ink)" }}
                    >
                      <Tag size={14} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">{p.name}</div>
                      <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] uppercase tracking-[0.12em]">
                        {p.category}
                        {p.has_pages ? " · multi-page" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditing(p)}
                      className="p-1.5 hover:bg-[color:var(--ups-muted)] rounded-sm"
                      aria-label="Edit"
                      data-testid={`edit-product-${p.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="p-1.5 hover:bg-red-100 rounded-sm text-red-700"
                      aria-label="Delete"
                      data-testid={`delete-product-${p.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {p.description && (
                  <p className="text-xs text-[color:var(--ups-subtext)] mt-2 line-clamp-2">{p.description}</p>
                )}

                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-mono">
                  <div className="p-2" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
                    <div className="uppercase tracking-[0.12em] opacity-70">Sizes</div>
                    <div className="font-bold text-[color:var(--ups-ink)]">{sizes.length}</div>
                    <div className="truncate">{sizes.join(", ") || "—"}</div>
                  </div>
                  <div className="p-2" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
                    <div className="uppercase tracking-[0.12em] opacity-70">Papers</div>
                    <div className="font-bold text-[color:var(--ups-ink)]">{paperCount}</div>
                  </div>
                  <div className="p-2" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
                    <div className="uppercase tracking-[0.12em] opacity-70">Finishes</div>
                    <div className="font-bold text-[color:var(--ups-ink)]">{finishCount}</div>
                  </div>
                </div>

                <div className="mt-2 text-[11px] font-mono text-[color:var(--ups-subtext)]">
                  Min qty: {p.min_quantity || 1}
                  {p.quantity_multiple > 1 ? ` · multiples of ${p.quantity_multiple}` : ""}
                  {p.has_pages ? ` · pages ×${p.page_multiple}` : ""}
                </div>

                {p.pricing_notes && (
                  <div
                    className="mt-2 text-[11px] font-mono px-2 py-1 whitespace-pre-wrap"
                    style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
                  >
                    {p.pricing_notes}
                  </div>
                )}
                {!p.is_active && (
                  <div className="mt-2 inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]" style={{ background: "var(--ups-muted)", color: "var(--ups-subtext)" }}>
                    Inactive
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <ProductEditor
          product={editing?.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
};

/* =========================== Library Tabs =========================== */
const SizesTab = () => (
  <LibraryTab
    title="Sizes"
    subtitle="Global size library used across products. Each product selects from these (plus optional custom sizes)."
    testidPrefix="sizes"
    fetchFn={adminListSizes}
    createFn={adminCreateSize}
    updateFn={adminUpdateSize}
    deleteFn={adminDeleteSize}
    empty={{ id: "", label: "", width_in: 0, height_in: 0, multiplier: 1, is_active: true }}
    fields={[
      { key: "id", label: "ID", required: true, readOnlyOnEdit: true, hint: "e.g. 8.5x11 — stable slug used by products", colSpan: 2 },
      { key: "label", label: "Label", required: true, colSpan: 2, hint: "Human-readable name (e.g. '8.5 × 11 in')" },
      { key: "width_in", label: "Width (in)", type: "number", step: "0.01" },
      { key: "height_in", label: "Height (in)", type: "number", step: "0.01" },
      { key: "multiplier", label: "Size multiplier", type: "number", step: "0.01", colSpan: 2, hint: "Relative to 8.5×11 = 1.0. Determines how much of a letter sheet this size uses." },
    ]}
    columns={[
      { key: "id", label: "ID", mono: true, flex: "1fr" },
      { key: "label", label: "Label", flex: "2fr" },
      { key: "multiplier", label: "Mult", mono: true, align: "right", flex: "0.7fr", format: (v) => `${v}×` },
    ]}
  />
);

const PapersTab = () => (
  <LibraryTab
    title="Papers"
    subtitle="Paper stocks with retail unit price (per 8.5×11 sheet). Size multiplier scales this per product + size."
    testidPrefix="papers"
    fetchFn={adminListPapers}
    createFn={adminCreatePaper}
    updateFn={adminUpdatePaper}
    deleteFn={adminDeletePaper}
    empty={{ id: "", label: "", unit_price: 0, weight: "", finish: "", is_active: true }}
    fields={[
      { key: "id", label: "ID", required: true, readOnlyOnEdit: true, hint: "e.g. 100lb_gloss_text", colSpan: 2 },
      { key: "label", label: "Label", required: true, colSpan: 2 },
      { key: "unit_price", label: "$ per 8.5×11 sheet", type: "number", step: "0.01", colSpan: 2, hint: "Base retail price; size multiplier scales this at quote time." },
      { key: "weight", label: "Weight", placeholder: "e.g. 100lb, 14pt" },
      { key: "finish", label: "Finish", placeholder: "gloss / matte / uncoated" },
    ]}
    columns={[
      { key: "id", label: "ID", mono: true, flex: "1.3fr" },
      { key: "label", label: "Label", flex: "2fr" },
      { key: "weight", label: "Weight", mono: true, flex: "0.7fr" },
      { key: "unit_price", label: "$/sheet", mono: true, align: "right", flex: "0.8fr", format: (v) => fmtUSD(v) },
    ]}
  />
);

const FinishesTab = () => (
  <LibraryTab
    title="Finishes"
    subtitle="Finishing operations (fold, score, laminate, bind, etc.). Per-unit + flat surcharges."
    testidPrefix="finishes"
    fetchFn={adminListFinishes}
    createFn={adminCreateFinish}
    updateFn={adminUpdateFinish}
    deleteFn={adminDeleteFinish}
    empty={{ id: "", label: "", surcharge_per_unit: 0, surcharge_flat: 0, description: "", is_active: true }}
    fields={[
      { key: "id", label: "ID", required: true, readOnlyOnEdit: true, hint: "e.g. tri_fold", colSpan: 2 },
      { key: "label", label: "Label", required: true, colSpan: 2 },
      { key: "surcharge_per_unit", label: "$ per unit", type: "number", step: "0.01" },
      { key: "surcharge_flat", label: "$ flat", type: "number", step: "0.01" },
      { key: "description", label: "Description", type: "textarea", colSpan: 2 },
    ]}
    columns={[
      { key: "id", label: "ID", mono: true, flex: "1.3fr" },
      { key: "label", label: "Label", flex: "1.5fr" },
      { key: "surcharge_per_unit", label: "/unit", mono: true, align: "right", flex: "0.7fr", format: (v) => fmtUSD(v) },
      { key: "surcharge_flat", label: "flat", mono: true, align: "right", flex: "0.7fr", format: (v) => fmtUSD(v) },
    ]}
  />
);

/* =========================== Main Panel =========================== */
const TAB_SECTIONS = [
  {
    id: "ops", label: "Operations",
    tabs: [
      { id: "tracker", label: "Tracker", icon: Box },
      { id: "customers", label: "Customers", icon: Users2 },
      { id: "email-intake", label: "Email Intake", icon: Inbox },
      { id: "email-templates", label: "Email Templates", icon: MailIcon },
      { id: "stores", label: "Store Profiles", icon: StoreIcon },
      { id: "portfolio", label: "Portfolio", icon: CameraIcon },
      { id: "team", label: "Team", icon: UserCog },
      { id: "admin-users", label: "Admin Users", icon: Users, ownerOnly: true },
      { id: "domains", label: "Access & Discounts", icon: Globe },
    ],
  },
  {
    id: "inventory", label: "Inventory",
    tabs: [
      { id: "suppliers", label: "Suppliers", icon: Building2 },
      { id: "inventory-orders", label: "Inventory Orders", icon: ClipboardList },
      { id: "materials", label: "Materials", icon: Boxes },
    ],
  },
  {
    id: "crm", label: "Customers",
    tabs: [
      { id: "crm", label: "CRM", icon: Users },
    ],
  },
  {
    id: "teams", label: "Teams & Schedule", ownerOnly: true,
    tabs: [
      { id: "teams-schedule", label: "Teams & Schedule", icon: CalendarClock },
    ],
  },
  {
    id: "catalog", label: "Catalog & Pricing",
    tabs: [
      { id: "products", label: "Products", icon: Package },
      { id: "matrix", label: "Pricing Matrix", icon: Grid2x2 },
      { id: "internal-quote", label: "Internal Quote", icon: Calculator },
      { id: "library", label: "Material Library", icon: Layers },
      { id: "sizes", label: "Sizes", icon: Ruler },
      { id: "papers", label: "Papers", icon: Layers },
      { id: "finishes", label: "Finishes", icon: Sparkles },
    ],
  },
  {
    id: "insights", label: "Reports & Activity",
    tabs: [
      { id: "overview", label: "Overview", icon: LayoutDashboard },
      { id: "closings", label: "Daily Closings", icon: Banknote },
      { id: "reports", label: "Reports", icon: BarChart3 },
      { id: "sessions", label: "Sessions", icon: Users },
      { id: "orders", label: "Orders", icon: ShoppingBag },
    ],
  },
];

const AdminPanel = () => {
  const [authed, setAuthed] = useState(Boolean(getAdminToken()));
  const [currentUser, setCurrentUser] = useState(getAdminUser());
  const [tab, setTab] = useState("tracker");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authed) return;
    // Probe the token by fetching stats; 401 → wipe token and re-ask
    (async () => {
      try {
        setStats(await adminGetStats());
      } catch (e) {
        if (e?.response?.status === 401) {
          clearAdminToken();
          setAuthed(false);
          setCurrentUser(null);
          toast.error("Session expired. Please log in.");
        }
      }
    })();
  }, [authed, tab]);

  // Cross-tab navigation hook (used by Email Intake → Tracker jump).
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail?.tab;
      if (typeof next === "string" && next) setTab(next);
    };
    window.addEventListener("admin:switch-tab", handler);
    return () => window.removeEventListener("admin:switch-tab", handler);
  }, []);

  // QR-code deep-link from printed Job Ticket — `/admin?focus={jobId}` flips
  // to the Tracker tab and opens the drawer for that job (production-floor
  // scan flow).
  useEffect(() => {
    if (!authed) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (!focusId) return;
    setTab("tracker");
    // Wait a tick so TrackerTab has mounted before firing the open event.
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("tracker:open-job", { detail: { jobId: focusId } }));
      // Clean the URL so a refresh doesn't reopen the same drawer.
      params.delete("focus");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }, 200);
    return () => clearTimeout(t);
  }, [authed]);

  if (!authed) return <AdminLogin onAuthed={(u) => { setAuthed(true); setCurrentUser(u); }} />;

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    setCurrentUser(null);
    toast.success("Signed out");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ups-paper)" }}>
      <header
        className="sticky top-0 z-20 border-b"
        style={{ background: "var(--ups-ink)", borderColor: "var(--ups-ink)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/site-logo.webp"
              alt="Roswell Printhub · Admin"
              className="h-10 w-auto rounded-sm"
            />
            <div className="hidden sm:flex flex-col leading-tight text-white">
              <span className="font-display text-sm font-bold">Control Panel</span>
              <span className="text-[10px] font-mono opacity-70">internal · restricted</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/chat")}
              className="text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded-sm transition"
              data-testid="admin-goto-chat"
            >
              <Mail size={14} /> Open Chat
            </button>
            <button
              onClick={logout}
              className="text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded-sm transition"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-5">
          <div className="overline">Admin</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black leading-none tracking-tight mt-2">
            Hub Control Panel
          </h1>
        </div>

        {/* Two-tier navigation
            ── Top tier: section selector (Operations · Inventory · Catalog · Reports)
            ── Bottom tier: sub-tabs for the currently active section
           Clicking a section button that ISN'T already active jumps to that
           section's first sub-tab; re-clicking the active section is a no-op
           so users don't accidentally lose their current view. */}
        {(() => {
          const isOwner = currentUser?.role === "owner";
          const visibleSections = TAB_SECTIONS.filter((s) => !s.ownerOnly || isOwner);
          const activeSection =
            visibleSections.find((s) => s.tabs.some((t) => t.id === tab)) || visibleSections[0];
          return (
            <div className="mb-6" data-testid="admin-tabs">
              {/* Top tier: sections */}
              <div
                className="flex items-stretch overflow-x-auto border-2"
                style={{ borderColor: "var(--ups-ink)", background: "var(--ups-ink)" }}
                data-testid="admin-section-bar"
                role="tablist"
                aria-label="Admin sections"
              >
                {visibleSections.map((section) => {
                  const isActive = section.id === activeSection.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => {
                        if (isActive) return;
                        setTab(section.tabs[0].id);
                      }}
                      className="px-5 py-3 text-[12px] font-bold uppercase tracking-[0.18em] whitespace-nowrap transition relative flex items-center gap-2"
                      style={{
                        background: isActive ? "#FFB500" : "transparent",
                        color: isActive ? "var(--ups-ink)" : "rgba(255, 251, 235, 0.75)",
                      }}
                      data-testid={`admin-section-${section.id}`}
                    >
                      {section.label}
                      {isActive && (
                        <span
                          className="absolute left-0 right-0 -bottom-0.5 h-[3px]"
                          style={{ background: "var(--ups-ink)" }}
                          aria-hidden
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Bottom tier: sub-tabs for the active section */}
              <div
                className="flex items-stretch overflow-x-auto border-x-2 border-b-2"
                style={{
                  borderColor: "var(--ups-ink)",
                  background: "var(--ups-paper)",
                }}
                data-testid="admin-subtab-bar"
                role="tablist"
                aria-label={`${activeSection.label} sub-tabs`}
              >
                {activeSection.tabs.filter((t) => !t.ownerOnly || currentUser?.role === "owner").map((t) => {
                  const isActive = tab === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setTab(t.id)}
                      className="px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] whitespace-nowrap flex items-center gap-2 transition border-b-2"
                      style={{
                        color: isActive ? "var(--ups-ink)" : "var(--ups-subtext)",
                        borderBottomColor: isActive ? "#FFB500" : "transparent",
                        background: isActive ? "rgba(255, 181, 0, 0.08)" : "transparent",
                      }}
                      data-testid={`admin-tab-${t.id}`}
                    >
                      <Icon size={14} /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Tab content */}
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "tracker" && <TrackerTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "email-intake" && <EmailIntakeTab />}
        {tab === "email-templates" && <EmailTemplatesTab />}
        {tab === "stores" && <StoresTab />}
        {tab === "portfolio" && <PortfolioTab />}
        {tab === "reports" && <ReportsTab />}
        {tab === "suppliers" && <SuppliersTab />}
        {tab === "inventory-orders" && <InventoryOrdersTab />}
        {tab === "materials" && <MaterialsTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "matrix" && <PricingMatrixTab />}
        {tab === "library" && <MaterialLibraryTab />}
        {tab === "domains" && <DomainsTab />}
        {tab === "team" && <TeamMembersTab />}
        {tab === "admin-users" && <TeamTab />}
        {tab === "crm" && <CRMTab />}
        {tab === "teams-schedule" && <TeamsScheduleTab />}
        {tab === "closings" && <ClosingsTab />}
        {tab === "internal-quote" && <InternalQuoteTab />}
        {tab === "sizes" && <SizesTab />}
        {tab === "papers" && <PapersTab />}
        {tab === "finishes" && <FinishesTab />}
      </main>
    </div>
  );
};

export default AdminPanel;
